import { useMemo, useState } from 'react';
import {
    Button,
    Card,
    CardContent,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    IconButton,
    Paper,
    Stack,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    TextField,
    Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { useDataProvider, useNotify, type Identifier } from 'react-admin';

import { DesktopActionCell, DesktopActionHeaderCell } from '../admin/DesktopTableActions';
import { parseApiDateTime } from '../admin/dateTimeUtils';
import { BILLING_COUNTRY_CURRENCIES_RESOURCE, type BillingCountryCurrency } from './billingData';
import { formatUpdatedAt } from './billingUtils';

type CountryCurrencyDraft = {
    id?: Identifier;
    countryCode: string;
    countryName: string;
    currency: string;
    isAirwallexSupported: boolean;
};

type BillingCountryCurrenciesSectionProps = {
    countryCurrencies: BillingCountryCurrency[];
    onChanged: () => Promise<void>;
};

type CountryCurrencySortField = 'countryName' | 'countryCode' | 'currency' | 'isAirwallexSupported' | 'updatedAt';

type SortOrder = 'asc' | 'desc';

function emptyCountryCurrencyDraft(): CountryCurrencyDraft {
    return {
        countryCode: '',
        countryName: '',
        currency: '',
        isAirwallexSupported: false,
    };
}

export function BillingCountryCurrenciesSection({
    countryCurrencies,
    onChanged,
}: BillingCountryCurrenciesSectionProps) {
    const notify = useNotify();
    const dataProvider = useDataProvider();

    const [countryCurrencyDialogOpen, setCountryCurrencyDialogOpen] = useState(false);
    const [countryCurrencyDraft, setCountryCurrencyDraft] = useState<CountryCurrencyDraft>(
        emptyCountryCurrencyDraft(),
    );
    const [searchValue, setSearchValue] = useState('');
    const [showAirwallexOnly, setShowAirwallexOnly] = useState(false);
    const [sortField, setSortField] = useState<CountryCurrencySortField>('countryName');
    const [sortOrder, setSortOrder] = useState<SortOrder>('asc');

    const visibleCountryCurrencies = useMemo(() => {
        const normalizedSearch = searchValue.trim().toLowerCase();
        const filteredBySupport = showAirwallexOnly
            ? countryCurrencies.filter((countryCurrency) => countryCurrency.isAirwallexSupported)
            : countryCurrencies;
        const filtered = normalizedSearch
            ? filteredBySupport.filter((countryCurrency) =>
                  [
                      countryCurrency.countryName,
                      countryCurrency.countryCode,
                      countryCurrency.currency,
                      countryCurrency.isAirwallexSupported ? 'supported' : 'not supported',
                  ].some((value) => String(value).toLowerCase().includes(normalizedSearch)),
              )
            : filteredBySupport;

        return [...filtered].sort((left, right) => {
            const direction = sortOrder === 'asc' ? 1 : -1;

            if (sortField === 'isAirwallexSupported') {
                const leftValue = left.isAirwallexSupported ? 1 : 0;
                const rightValue = right.isAirwallexSupported ? 1 : 0;
                return (leftValue - rightValue) * direction;
            }

            if (sortField === 'updatedAt') {
                const leftValue = parseApiDateTime(left.updatedAt)?.getTime() ?? 0;
                const rightValue = parseApiDateTime(right.updatedAt)?.getTime() ?? 0;
                return (leftValue - rightValue) * direction;
            }

            const leftValue = String(left[sortField] ?? '').toLowerCase();
            const rightValue = String(right[sortField] ?? '').toLowerCase();
            return leftValue.localeCompare(rightValue) * direction;
        });
    }, [countryCurrencies, searchValue, showAirwallexOnly, sortField, sortOrder]);

    const handleSort = (field: CountryCurrencySortField) => {
        if (sortField === field) {
            setSortOrder((current) => (current === 'asc' ? 'desc' : 'asc'));
            return;
        }

        setSortField(field);
        setSortOrder('asc');
    };

    const openCreateCountryCurrencyDialog = () => {
        setCountryCurrencyDraft(emptyCountryCurrencyDraft());
        setCountryCurrencyDialogOpen(true);
    };

    const openEditCountryCurrencyDialog = (countryCurrency: BillingCountryCurrency) => {
        setCountryCurrencyDraft({
            id: countryCurrency.id,
            countryCode: countryCurrency.countryCode,
            countryName: countryCurrency.countryName,
            currency: countryCurrency.currency,
            isAirwallexSupported: countryCurrency.isAirwallexSupported,
        });
        setCountryCurrencyDialogOpen(true);
    };

    const handleDelete = async (countryCurrency: BillingCountryCurrency) => {
        try {
            await dataProvider.delete(BILLING_COUNTRY_CURRENCIES_RESOURCE, {
                id: countryCurrency.id,
                previousData: countryCurrency,
            });
            notify('Country currency deleted', { type: 'info' });
            await onChanged();
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Delete failed';
            notify(message, { type: 'error' });
        }
    };

    const handleSaveCountryCurrency = async () => {
        const payload = {
            countryCode: countryCurrencyDraft.countryCode.trim().toUpperCase(),
            countryName: countryCurrencyDraft.countryName.trim(),
            currency: countryCurrencyDraft.currency.trim().toUpperCase(),
            isAirwallexSupported: countryCurrencyDraft.isAirwallexSupported,
        };

        if (!payload.countryCode || !payload.countryName || !payload.currency) {
            notify('Country code, country name, and currency are required', { type: 'warning' });
            return;
        }

        try {
            if (countryCurrencyDraft.id) {
                await dataProvider.update(BILLING_COUNTRY_CURRENCIES_RESOURCE, {
                    id: countryCurrencyDraft.id,
                    data: payload,
                    previousData: countryCurrencies.find(
                        (countryCurrency) => countryCurrency.id === countryCurrencyDraft.id,
                    ),
                });
                notify('Country currency updated', { type: 'info' });
            } else {
                await dataProvider.create(BILLING_COUNTRY_CURRENCIES_RESOURCE, { data: payload });
                notify('Country currency created', { type: 'info' });
            }

            setCountryCurrencyDialogOpen(false);
            await onChanged();
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Unable to save country currency';
            notify(message, { type: 'error' });
        }
    };

    return (
        <>
            <Stack spacing={2}>
                <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={2}>
                    <Stack spacing={1} sx={{ flex: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                            Country-currency mappings define the sale currency and whether Airwallex checkout can
                            be used for that market.
                        </Typography>
                        <TextField
                            label="Search country currencies"
                            value={searchValue}
                            onChange={(event) => setSearchValue(event.target.value)}
                            placeholder="Search by country, code, currency, or support status"
                            size="small"
                            sx={{ maxWidth: 420 }}
                        />
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={showAirwallexOnly}
                                    onChange={(event) => setShowAirwallexOnly(event.target.checked)}
                                />
                            }
                            label="Airwallex-enabled countries only"
                        />
                    </Stack>
                    <Stack alignItems={{ xs: 'stretch', sm: 'flex-end' }} spacing={1}>
                        <Typography variant="caption" color="text.secondary">
                            Showing {visibleCountryCurrencies.length} of {countryCurrencies.length}
                        </Typography>
                        <Button variant="contained" onClick={openCreateCountryCurrencyDialog}>
                            Add Country Currency
                        </Button>
                    </Stack>
                </Stack>

                <Stack spacing={1.5} sx={{ display: { xs: 'flex', lg: 'none' } }}>
                    {visibleCountryCurrencies.map((countryCurrency) => (
                        <Card key={countryCurrency.id} variant="outlined">
                            <CardContent>
                                <Stack spacing={2}>
                                    <Stack spacing={0.5}>
                                        <Typography variant="subtitle1">{countryCurrency.countryName}</Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {countryCurrency.countryCode} • {countryCurrency.currency}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Updated: {formatUpdatedAt(countryCurrency.updatedAt)}
                                        </Typography>
                                    </Stack>
                                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} useFlexGap flexWrap="wrap">
                                        <Chip
                                            label={countryCurrency.isAirwallexSupported ? 'Status: Supported' : 'Status: Not supported'}
                                            color={countryCurrency.isAirwallexSupported ? 'success' : 'default'}
                                            size="small"
                                        />
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            onClick={() => openEditCountryCurrencyDialog(countryCurrency)}
                                        >
                                            Edit
                                        </Button>
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            color="error"
                                            onClick={() => void handleDelete(countryCurrency)}
                                        >
                                            Delete
                                        </Button>
                                    </Stack>
                                </Stack>
                            </CardContent>
                        </Card>
                    ))}
                    {visibleCountryCurrencies.length === 0 ? (
                        <Card variant="outlined">
                            <CardContent>
                                <Typography variant="body2" color="text.secondary">
                                    No country-currency mappings match the current search.
                                </Typography>
                            </CardContent>
                        </Card>
                    ) : null}
                </Stack>
                <TableContainer component={Paper} variant="outlined" sx={{ display: { xs: 'none', lg: 'block' } }}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell sortDirection={sortField === 'countryName' ? sortOrder : false}>
                                    <TableSortLabel
                                        active={sortField === 'countryName'}
                                        direction={sortField === 'countryName' ? sortOrder : 'asc'}
                                        onClick={() => handleSort('countryName')}
                                    >
                                        Country
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell sortDirection={sortField === 'countryCode' ? sortOrder : false}>
                                    <TableSortLabel
                                        active={sortField === 'countryCode'}
                                        direction={sortField === 'countryCode' ? sortOrder : 'asc'}
                                        onClick={() => handleSort('countryCode')}
                                    >
                                        Code
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell sortDirection={sortField === 'currency' ? sortOrder : false}>
                                    <TableSortLabel
                                        active={sortField === 'currency'}
                                        direction={sortField === 'currency' ? sortOrder : 'asc'}
                                        onClick={() => handleSort('currency')}
                                    >
                                        Currency
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell sortDirection={sortField === 'isAirwallexSupported' ? sortOrder : false}>
                                    <TableSortLabel
                                        active={sortField === 'isAirwallexSupported'}
                                        direction={sortField === 'isAirwallexSupported' ? sortOrder : 'asc'}
                                        onClick={() => handleSort('isAirwallexSupported')}
                                    >
                                        Airwallex
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell sortDirection={sortField === 'updatedAt' ? sortOrder : false}>
                                    <TableSortLabel
                                        active={sortField === 'updatedAt'}
                                        direction={sortField === 'updatedAt' ? sortOrder : 'asc'}
                                        onClick={() => handleSort('updatedAt')}
                                    >
                                        Updated
                                    </TableSortLabel>
                                </TableCell>
                                <DesktopActionHeaderCell>Edit</DesktopActionHeaderCell>
                                <DesktopActionHeaderCell>Delete</DesktopActionHeaderCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {visibleCountryCurrencies.map((countryCurrency) => (
                                <TableRow key={countryCurrency.id} hover>
                                    <TableCell>{countryCurrency.countryName}</TableCell>
                                    <TableCell>{countryCurrency.countryCode}</TableCell>
                                    <TableCell>{countryCurrency.currency}</TableCell>
                                    <TableCell>
                                        <Chip
                                            label={countryCurrency.isAirwallexSupported ? 'Supported' : 'Not supported'}
                                            color={countryCurrency.isAirwallexSupported ? 'success' : 'default'}
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell>{formatUpdatedAt(countryCurrency.updatedAt)}</TableCell>
                                    <DesktopActionCell>
                                        <IconButton
                                            aria-label="Edit country currency"
                                            onClick={() => openEditCountryCurrencyDialog(countryCurrency)}
                                        >
                                            <EditOutlinedIcon fontSize="small" />
                                        </IconButton>
                                    </DesktopActionCell>
                                    <DesktopActionCell>
                                        <IconButton
                                            aria-label="Delete country currency"
                                            onClick={() => void handleDelete(countryCurrency)}
                                        >
                                            <DeleteOutlineIcon fontSize="small" />
                                        </IconButton>
                                    </DesktopActionCell>
                                </TableRow>
                            ))}
                            {visibleCountryCurrencies.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={7}>
                                        <Typography variant="body2" color="text.secondary">
                                            No country-currency mappings match the current search.
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : null}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Stack>

            <Dialog
                open={countryCurrencyDialogOpen}
                onClose={() => setCountryCurrencyDialogOpen(false)}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle>{countryCurrencyDraft.id ? 'Edit Country Currency' : 'Add Country Currency'}</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ pt: 1 }}>
                        <TextField
                            label="Country Name"
                            value={countryCurrencyDraft.countryName}
                            onChange={(event) =>
                                setCountryCurrencyDraft((current) => ({
                                    ...current,
                                    countryName: event.target.value,
                                }))
                            }
                            fullWidth
                        />
                        <TextField
                            label="Country Code"
                            value={countryCurrencyDraft.countryCode}
                            onChange={(event) =>
                                setCountryCurrencyDraft((current) => ({
                                    ...current,
                                    countryCode: event.target.value,
                                }))
                            }
                            helperText="Two-letter ISO code, for example US or AU"
                            fullWidth
                        />
                        <TextField
                            label="Currency"
                            value={countryCurrencyDraft.currency}
                            onChange={(event) =>
                                setCountryCurrencyDraft((current) => ({
                                    ...current,
                                    currency: event.target.value,
                                }))
                            }
                            helperText="Three-letter code, for example USD or AUD"
                            fullWidth
                        />
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={countryCurrencyDraft.isAirwallexSupported}
                                    onChange={(event) =>
                                        setCountryCurrencyDraft((current) => ({
                                            ...current,
                                            isAirwallexSupported: event.target.checked,
                                        }))
                                    }
                                />
                            }
                            label="Airwallex supported"
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCountryCurrencyDialogOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={() => void handleSaveCountryCurrency()}>
                        Save Country Currency
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}